import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Typography,
  Collapse,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../assets/icon.png";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import theme from "../theme/Theme";
import { listPrograms } from "../../apis/programsApi";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const userType = localStorage.getItem("userType");
  const userName = localStorage.getItem("userName");
  const { logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [programsAnchorEl, setProgramsAnchorEl] = useState<null | HTMLElement>(null);
  const [programs, setPrograms] = useState<Array<{ id: string; title: string }>>([]);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await listPrograms();
        const filteredData = data.filter((p: any) => p.isActive);
        setPrograms(filteredData.map((p: any) => ({ id: p.id, title: p.title })));
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
    localStorage.removeItem("userType");
    localStorage.removeItem("userName");
    navigate("/");
    handleMenuClose();
  };

  const handleMenuOpen = () => {
    setMenuOpen(!menuOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openProgramsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setProgramsAnchorEl(event.currentTarget);
  };

  const closeProgramsMenu = () => {
    setProgramsAnchorEl(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMenuOpen(false);
    handleMenuClose();
  };

  const navbarStyle = {
    backgroundColor: "#3c3759",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    height: "80px",
    width: "100%",
  };

  const buttonStyle = {
    color: "#ffffff",
    mx: 1,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
    },
  };

  const getMobileMenuItems = () => {
    const baseItems = [
      { text: "Home", path: "/" },
      { text: "About", path: "/about" },
      { text: "Listings", path: "/listings" },
      { text: "Memberships", path: "/memberships" },
    ];

    if (token && userType === "user") {
      baseItems.push({ text: "Dashboard", path: "/dashboard" });
    }

    return baseItems;
  };

  return (
    <AppBar position="static" sx={navbarStyle}>
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          <img
            src={Logo}
            alt="The Bat Cage"
            style={{
              width: "100px",
            }}
          />
        </Box>

        {isMobile ? (
          <>
            <IconButton
              color="inherit"
              aria-label="menu"
              sx={{ color: "#ffffff" }}
              onClick={handleMenuOpen}
            >
              <MenuIcon />
            </IconButton>

            <Drawer
              anchor="right"
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              PaperProps={{
                sx: {
                  width: "70%",
                  maxWidth: "300px",
                  backgroundColor: "#3c3759",
                  color: "#ffffff",
                },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                <IconButton
                  onClick={() => setMenuOpen(false)}
                  sx={{ color: "#ffffff" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <List>
                {token && userType === "user" && (
                  <ListItem
                    sx={{
                      justifyContent: "center",
                      borderBottom: "1px solid rgba(255,255,255,0.2)",
                      py: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        color: "#ffffff",
                      }}
                    >
                      <AccountCircleIcon sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="h6">
                        {userName || "User Profile"}
                      </Typography>
                    </Box>
                  </ListItem>
                )}

                {getMobileMenuItems().map((item) => (
                  <ListItem
                    key={item.text}
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.2)",
                      },
                    }}
                  >
                    <ListItemText primary={item.text} />
                  </ListItem>
                ))}

                {/* Mobile Programs accordion */}
                <ListItem
                  onClick={() => setMobileProgramsOpen(!mobileProgramsOpen)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <ListItemText primary="Programs" />
                  {mobileProgramsOpen ? <ExpandLess sx={{ color: "#fff" }} /> : <ExpandMore sx={{ color: "#fff" }} />}
                </ListItem>
                <Collapse in={mobileProgramsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {programs.map((p) => (
                      <ListItem
                        key={p.id}
                        onClick={() => handleNavigate(`/programs/${p.id}`)}
                        sx={{ pl: 4, "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                      >
                        <ListItemText primary={p.title} />
                      </ListItem>
                    ))}
                    {programs.length === 0 && (
                      <ListItem sx={{ pl: 4 }}>
                        <ListItemText primary="No programs" />
                      </ListItem>
                    )}
                  </List>
                </Collapse>

                <Divider
                  sx={{ backgroundColor: "rgba(255,255,255,0.2)", my: 1 }}
                />

                {token && userType === "user" ? (
                  <Box sx={{ px: 2 }}>
                    <ListItem
                      onClick={handleLogout}
                      sx={{
                        backgroundColor: "#f27143",
                        "&:hover": {
                          backgroundColor: "#e6633a",
                        },
                        borderRadius: "4px",
                        width: "100%",
                        justifyContent: "center",
                        mt: 1,
                      }}
                    >
                      <ListItemText
                        primary="Logout"
                        primaryTypographyProps={{
                          align: "center",
                          sx: { color: "#ffffff" },
                        }}
                      />
                    </ListItem>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      px: 2,
                      gap: 1,
                    }}
                  >
                    <ListItem
                      onClick={() => handleNavigate("/login")}
                      sx={{
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "4px",
                        justifyContent: "center",
                      }}
                    >
                      <ListItemText
                        primary="Login"
                        primaryTypographyProps={{
                          align: "center",
                          sx: { color: "#ffffff" },
                        }}
                      />
                    </ListItem>
                    <ListItem
                      onClick={() => handleNavigate("/signup")}
                      sx={{
                        backgroundColor: "#068cc1",
                        "&:hover": {
                          backgroundColor: "#0d6186",
                        },
                        borderRadius: "4px",
                        justifyContent: "center",
                      }}
                    >
                      <ListItemText
                        primary="Signup"
                        primaryTypographyProps={{
                          align: "center",
                          sx: { color: "#ffffff" },
                        }}
                      />
                    </ListItem>
                  </Box>
                )}
              </List>
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <>
              <Button sx={buttonStyle} onClick={() => navigate("/")}>
                Home
              </Button>
              <Button sx={buttonStyle} onClick={() => navigate("/about")}>
                About
              </Button>
              <Button sx={buttonStyle} onClick={() => navigate("/listings")}>
                Listings
              </Button>

              <Button
                    sx={buttonStyle}
                    onClick={() => navigate("/memberships")}
                  >
                    Memberships
                  </Button>
              <Box onMouseLeave={closeProgramsMenu}>
                <Button
                  sx={buttonStyle}
                  onClick={(e) => (programs.length ? openProgramsMenu(e as any) : navigate("/programs"))}
                  onMouseEnter={(e) => programs.length && openProgramsMenu(e as any)}
                >
                  Programs
                </Button>
                <Menu
                  anchorEl={programsAnchorEl}
                  open={Boolean(programsAnchorEl)}
                  onClose={closeProgramsMenu}
                  TransitionProps={{ timeout: 150 }}
                  sx={{ "& .MuiPaper-root": { backgroundColor: "#3c3759", color: "#ffffff" } }}
                >
                  {programs.map((p) => (
                    <MenuItem
                      key={p.id}
                      onClick={() => {
                        navigate(`/programs/${p.id}`);
                        closeProgramsMenu();
                      }}
                      sx={{ color: "#ffffff", "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" } }}
                    >
                      {p.title}
                    </MenuItem>
                  ))}
                  {programs.length === 0 && (
                    <MenuItem disabled sx={{ color: "#ffffff" }}>
                      No programs
                    </MenuItem>
                  )}
                </Menu>
              </Box>
              {token && userType === "user" ? (
                <>
                  <Button
                    sx={buttonStyle}
                    onClick={() => navigate("/dashboard")}
                  >
                    Dashboard
                  </Button>


                  {/* User Profile Dropdown */}
                  <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                    <Button
                      onClick={handleUserMenuOpen}
                      sx={{
                        color: "#ffffff",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.2)",
                        },
                      }}
                      startIcon={<AccountCircleIcon />}
                    >
                      {userName || "Profile"}
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      sx={{
                        "& .MuiPaper-root": {
                          backgroundColor: "#3c3759",
                          color: "#ffffff",
                        },
                      }}
                    >
                      <MenuItem
                        onClick={handleLogout}
                        sx={{
                          color: "#ffffff",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.2)",
                          },
                        }}
                      >
                        Logout
                      </MenuItem>
                    </Menu>
                  </Box>
                </>
              ) : (
                <>
                  <Button sx={buttonStyle} onClick={() => navigate("/login")}>
                    Log In
                  </Button>
                  <Button variant="primary" onClick={() => navigate("/signup")}>
                    Sign Up
                  </Button>
                </>
              )}
            </>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
